// ============================================================================
// Small, dependency-free formatting helpers for the client report.
// Locale-aware number formatting via Intl; date formatting kept light.
// ============================================================================
import type { BodyMetricKey } from "./types";
import { intlLocale } from "../../i18n/config";

/** Round + format a number with a fixed number of decimals (locale grouping). */
export function fmtNum(
  value: number,
  decimals = 0,
  locale?: string,
): string {
  return value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** A signed delta string, e.g. "+1.2" / "-0.40" / "0". */
export function fmtSigned(value: number, decimals = 1, locale?: string): string {
  const rounded = Number(value.toFixed(decimals));
  if (rounded === 0) return fmtNum(0, decimals, locale);
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${fmtNum(rounded, decimals, locale)}`;
}

/** Default decimal precision per body metric (weight 1dp, ratios 2dp …). */
export function metricDecimals(key: BodyMetricKey): number {
  switch (key) {
    case "weight":
    case "body_fat":
    case "bmi":
      return 1;
    case "whr":
    case "whtr":
      return 2;
    default:
      return 1;
  }
}

/** A short unit suffix per body metric (kg / % / blank for ratios). */
export function metricUnit(key: BodyMetricKey): string {
  switch (key) {
    case "weight":
      return " kg";
    case "body_fat":
      return "%";
    default:
      return "";
  }
}

/**
 * Format an ISO date(time) to a friendly, locale-aware label.
 * Falls back to the raw string if it can't be parsed.
 */
export function fmtDate(iso: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(intlLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Short axis tick for a series x-value (ISO) → "Jan", "Mar" … */
export function fmtAxisDate(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(locale), { month: "short", day: "numeric" });
}

/** Clamp a number to a [min, max] range. */
export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
